// Resolve the best available poster image for a library card.
//
// Preference order: the module's own thumbnail_url, then the hero video's YouTube frame, then
// nothing (the card renders a branded gradient). A thumbnail_url can point at our private
// documents bucket, so it's run through resolveAssetUrl (which presigns private URLs and returns
// public/YouTube URLs unchanged). Only the async resolution touches state; `src`/`loading` for the
// current candidate are derived during render, so no state is set synchronously in the effect.

import { useEffect, useState } from 'react';
import { resolveAssetUrl, isPrivateAssetUrl } from '../utils/assets';
import { youTubeThumbnailUrl } from '../utils/youtube';
import type { ModuleSummary } from '../types';

// requestDownloadUrl signs private-bucket URLs for 300s; re-mint with a margin so a card that is
// fetched lazily long after mount (scrolled into view) never loads an already-expired URL.
const REFRESH_AFTER_MS = 4 * 60_000;

export function useModuleThumbnail(module: Pick<ModuleSummary, 'thumbnail_url' | 'hero_video_url'>): {
  src: string | null;
  loading: boolean;
} {
  const candidate = module.thumbnail_url || youTubeThumbnailUrl(module.hero_video_url) || null;
  const [resolved, setResolved] = useState<{ candidate: string | null; src: string | null }>({
    candidate: null,
    src: null,
  });

  useEffect(() => {
    if (!candidate) return; // nothing to resolve; state is derived below
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const resolve = () => {
      resolveAssetUrl(candidate)
        .then(url => {
          if (cancelled) return;
          setResolved({ candidate, src: url || null });
          // Only private-bucket URLs are presigned (and thus expire); public/YouTube URLs are
          // returned unchanged and never need refreshing.
          if (isPrivateAssetUrl(candidate)) timer = setTimeout(resolve, REFRESH_AFTER_MS);
        })
        .catch(() => {
          if (!cancelled) setResolved({ candidate, src: null }); // fall back to the gradient
        });
    };
    resolve();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [candidate]);

  const isResolvedForCandidate = resolved.candidate === candidate;
  return {
    src: candidate && isResolvedForCandidate ? resolved.src : null,
    loading: Boolean(candidate) && !isResolvedForCandidate,
  };
}
